import { Columns } from '@spark-web/columns';
import { Combobox } from '@spark-web/combobox';
import { Field } from '@spark-web/field';
import { Select } from '@spark-web/select';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { TextInput } from '@spark-web/text-input';
import { TextLinkButton } from '@spark-web/text-link';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useFetcher } from 'react-router';

import { RULE_POSTCODE_PATTERN } from '~/constants';
import { AddressState, AddressSuggestionsResult } from '~/gql/graphql';
import { useDebounce } from '~/hooks/useDebounce';
import type { loader as addressLoader } from '~/routes/api+/marketplace+/address';
import type { loader as addressDetailLoader } from '~/routes/api+/marketplace+/address.detail';
import { CustomerAddress } from '~/types/marketplace';

type Option = {
  label: string;
  value: string;
};

type AddressSuggestionInputProps = {
  helperText?: string;
  defaultInput?: 'search' | 'manual';
  onAddressSelect?: () => void;
};

function getStateList() {
  return Object.values(AddressState).map((state) => ({ label: state, value: state }));
}

type AddressDetail = CustomerAddress & { address?: Option };

export const AddressSuggestionInput = ({
  helperText,
  defaultInput = 'search',
  onAddressSelect,
}: AddressSuggestionInputProps) => {
  const { control, setValue } = useFormContext<AddressDetail>();
  const addressFetcher = useFetcher<typeof addressLoader>();
  const addressDetailFetcher = useFetcher<typeof addressDetailLoader>();
  const [inputValue, setInputValue] = useState('');
  const [debouncedInputValue] = useDebounce(inputValue, 500);
  const [inputType, setInputType] = useState<'manual' | 'search'>(defaultInput);

  const isLoading = addressFetcher.state === 'loading';

  useEffect(() => {
    if (!debouncedInputValue) return;

    addressFetcher.submit({ address: debouncedInputValue }, { method: 'get', action: '/api/marketplace/address' });
  }, [debouncedInputValue]);

  useEffect(() => {
    const addressData = addressDetailFetcher.data;
    if (addressDetailFetcher.state !== 'loading' && addressDetailFetcher.data) {
      setManualInputValues(
        {
          addressLine1: `${addressData?.streetNumber} ${addressData?.streetName}`,
          addressLine2: addressData?.unitNumber ?? '',
          suburb: addressData?.suburb ?? '',
          state: addressData?.state ?? '',
          postcode: addressData?.postcode ?? '',
        },
        { shouldValidate: true },
      );
    }
  }, [addressDetailFetcher]);

  const addresses = useMemo(() => {
    const addressData = addressFetcher.data ?? ([] as AddressSuggestionsResult[]);

    return [
      ...(addressData.length > 0
        ? addressData.map((address) => ({
            label: address?.formattedAddress ?? '',
            value: address?.formattedAddress ?? '',
          }))
        : []),
      ...(inputValue
        ? [
            {
              label: `Can't find your address?`,
              value: 'manual',
            },
          ]
        : []),
    ];
  }, [addressFetcher, inputValue]);

  const setManualInputValues = (address: AddressDetail, options = {}) => {
    return Object.entries(address).forEach(([key, value]) => setValue(key as keyof AddressDetail, value, options));
  };

  const handleOnChange = (selected: Option | null) => {
    if (!selected) return;

    if (selected.value === 'manual') {
      switchInputType('manual');
      return;
    }

    setValue('address', selected, { shouldValidate: true });
    onAddressSelect?.();
    addressDetailFetcher.submit(
      { address: selected.value },
      { method: 'get', action: '/api/marketplace/address/detail' },
    );
  };

  const switchInputType = (type: 'search' | 'manual') => {
    setInputType(type);
    setManualInputValues(
      {
        addressLine1: '',
        addressLine2: '',
        suburb: '',
        state: '',
        postcode: '',
        address: undefined,
      },
      { shouldValidate: true },
    );
  };

  return (
    <>
      {inputType === 'search' && (
        <Stack gap="small">
          <Controller
            control={control}
            rules={{ required: true }}
            name="address"
            render={({ fieldState, field: { value } }) => (
              <Field label="Home address" message={fieldState?.error?.message}>
                <Combobox
                  items={addresses}
                  placeholder="Search for your address"
                  onInputChange={(value) => setInputValue(value)}
                  inputValue={inputValue}
                  isLoading={isLoading}
                  onChange={handleOnChange}
                  value={value}
                  disableFilter
                />
              </Field>
            )}
          />
          {helperText && <Text size="xsmall">{helperText}</Text>}
        </Stack>
      )}
      {inputType === 'manual' && (
        <>
          <Stack gap="xsmall">
            <Controller
              control={control}
              name="addressLine1"
              rules={{ required: true }}
              render={({ field: { onChange, value, onBlur, name }, fieldState }) => (
                <Field
                  label="Home address"
                  tone={fieldState?.error ? 'critical' : 'neutral'}
                  message={fieldState?.error?.message}
                >
                  <TextInput
                    name={name}
                    value={value}
                    onBlur={onBlur}
                    onChange={onChange}
                    placeholder="Street address"
                  />
                </Field>
              )}
            />
            <Controller
              control={control}
              name="addressLine2"
              render={({ field: { onChange, value, onBlur, name }, fieldState }) => (
                <Field label="" tone={fieldState?.error ? 'critical' : 'neutral'} message={fieldState?.error?.message}>
                  <TextInput
                    name={name}
                    value={value}
                    onBlur={onBlur}
                    onChange={onChange}
                    placeholder="Apartment, unit, etc. (optional)"
                  />
                </Field>
              )}
            />
          </Stack>

          <Controller
            control={control}
            name="suburb"
            rules={{ required: true }}
            render={({ field: { onChange, value, onBlur, name }, fieldState }) => (
              <Field
                label="Suburb"
                tone={fieldState?.error ? 'critical' : 'neutral'}
                message={fieldState?.error?.message}
              >
                <TextInput name={name} value={value ?? ''} onBlur={onBlur} onChange={onChange} />
              </Field>
            )}
          />

          <Columns gap="large">
            <Controller
              control={control}
              name="state"
              rules={{ required: true }}
              render={({ field: { onChange, value, onBlur, name }, fieldState }) => (
                <Field
                  label="State"
                  tone={fieldState?.error ? 'critical' : 'neutral'}
                  message={fieldState?.error?.message}
                >
                  <Select
                    name={name}
                    options={getStateList()}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value ?? ''}
                  />
                </Field>
              )}
            />
            <Controller
              control={control}
              name="postcode"
              rules={{ required: true, ...RULE_POSTCODE_PATTERN }}
              render={({ field: { onChange, value, onBlur, name }, fieldState }) => (
                <Field
                  label="Postcode"
                  tone={fieldState?.error ? 'critical' : 'neutral'}
                  message={fieldState?.error?.message}
                >
                  <TextInput name={name} value={value ?? ''} onBlur={onBlur} onChange={onChange} />
                </Field>
              )}
            />
          </Columns>
          <Text size="small">
            Would you like to{' '}
            <TextLinkButton onClick={() => switchInputType('search')}>search for your address?</TextLinkButton>
          </Text>
        </>
      )}
    </>
  );
};
